// Package imports:
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
// Style imports:
// import './SortFilterTable.scss';
// Component imports:
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import MultiSelectFilter from './components/MultiSelectFilter';

// Constants and type definitions:
export type AlignOptions = 'left' | 'center' | 'right';
const ALL_OPTIONS_VALUE = 'All';

interface ISortingInfo {
    sortColumnIndex: number | null;
    sortColumnTitle: string | JSX.Element | null;
    reverseSort: boolean;
}

// Define the filtering state type using a discriminated union.
type FilteringInfoItem =
    | { type: 'select' | 'string'; value: string }
    | { type: 'multiselect'; value: string[] };

type FilteringInfo = {
    [filterId: string]: FilteringInfoItem;
};

export interface IColumn<T> {
    title: string | JSX.Element;
    dataAccessor(dataItem: T): any;
    textAlign?: AlignOptions;
    overrideTd?: boolean;
    cellClassName?: string;
    isColorTimestamp?: (a: T) => boolean;
    sortable?: (a: T, b: T) => number;
    simpleSortable?: (a: T) => number | string | null;
    sortTiebreaker?: (a: T, b: T) => number;
    filter?: {
        id: string;
        // Either an array of options or the literal 'string'
        type: string[] | 'string';
        // NEW: if true, allows multiple selection.
        multiple?: boolean;
        stringValue: (a: T) => string | null;
        // For non-multiple filters, you can provide a default value.
        forcedValue?: string;
        onChange?: (value: string | string[]) => void;
    };
}

interface IProps<T> {
    data: Array<T>;
    isLoading: boolean;
    isError: boolean;
    columns: Array<IColumn<T>>;
    onRowClick?: (a: T) => void;
    rowId?: (a: T) => string | undefined;
    rowClassName?: (a: T) => string | undefined;
}

const SortFilterTable = <T extends any>({
    data,
    isLoading,
    isError,
    columns,
    onRowClick,
    rowId,
    rowClassName
}: IProps<T>) => {
    // Set states.
    const [sortingInfo, setSortingInfo] = useState<ISortingInfo>({
        sortColumnIndex: null,
        sortColumnTitle: null,
        reverseSort: false
    });

    const [filterInfoMap, setFilterInfoMap] = useState<FilteringInfo>({});

    const isFilterable = useMemo(
        () => columns.some(col => col.filter !== undefined),
        [columns]
    );

    // When columns change (due to filter, for example), check if sort still applies.
    useEffect(() => {
        const { sortColumnIndex, sortColumnTitle, reverseSort } = sortingInfo;
        if (sortColumnIndex === null || sortColumnTitle === null) return;
        if (columns[sortColumnIndex]?.title === sortColumnTitle) return;
        const indexOfColumnWithTitle = columns.findIndex(
            column => column.title === sortColumnTitle
        );
        if (indexOfColumnWithTitle >= 0) {
            setSortingInfo({
                sortColumnIndex: indexOfColumnWithTitle,
                sortColumnTitle: columns[indexOfColumnWithTitle].title,
                reverseSort
            });
        } else {
            setSortingInfo({
                sortColumnIndex: null,
                sortColumnTitle: null,
                reverseSort: false
            });
        }
        // eslint-disable-next-line
    }, [columns]);

    // Helper function for text alignment.
    const getAlignStyles = (align?: AlignOptions) => ({ textAlign: align ?? 'right' });

    // Tiebreaker helper function.
    const tiebreakerControler = (
        sortTiebreaker: ((a: T, b: T) => number) | undefined,
        datum1: T,
        datum2: T
    ) => {
        if (sortTiebreaker) return sortTiebreaker(datum1, datum2);
        return 0;
    };

    // Simple string sort helper.
    const simpleSortStringkControler = (
        simpleDatum1: string,
        simpleDatum2: string,
        datum1: T,
        datum2: T,
        reverse: (a: number) => number,
        sortTiebreaker: ((a: T, b: T) => number) | undefined
    ) => {
        if (simpleDatum1 === simpleDatum2) {
            return tiebreakerControler(sortTiebreaker, datum1, datum2);
        } else if (simpleDatum1 < simpleDatum2) {
            return reverse(-1);
        } else {
            return reverse(1);
        }
    };

    // Simple number sort helper.
    const simpleSortNumberSort = (
        simpleDatum1: number,
        simpleDatum2: number,
        datum1: T,
        datum2: T,
        reverse: (a: number) => number,
        sortTiebreaker: ((a: T, b: T) => number) | undefined
    ) => {
        const diff = simpleDatum1 - simpleDatum2;
        if (diff === 0) return tiebreakerControler(sortTiebreaker, datum1, datum2);
        return reverse(diff);
    };

    const sortDataArray = (data: T[]) =>
        [...data].sort((datum1, datum2) => {
            const { sortColumnIndex, reverseSort } = sortingInfo;
            const reverse = (returnValue: number) => (reverseSort ? -returnValue : returnValue);

            if (sortColumnIndex === null) return 0;

            const { sortable, simpleSortable, sortTiebreaker } = columns[sortColumnIndex] ?? {};

            if (simpleSortable !== undefined) {
                const simpleDatum1 = simpleSortable(datum1),
                    simpleDatum2 = simpleSortable(datum2);
                if (simpleDatum1 === null && simpleDatum2 === null)
                    return tiebreakerControler(sortTiebreaker, datum1, datum2);
                if (simpleDatum1 === null) return 1;
                if (simpleDatum2 === null) return -1;
                if (
                    typeof simpleDatum1 === 'string' &&
                    typeof simpleDatum2 === 'string'
                ) {
                    return simpleSortStringkControler(
                        simpleDatum1,
                        simpleDatum2,
                        datum1,
                        datum2,
                        reverse,
                        sortTiebreaker
                    );
                }
                if (
                    typeof simpleDatum1 === 'number' &&
                    typeof simpleDatum2 === 'number'
                ) {
                    return simpleSortNumberSort(
                        simpleDatum1,
                        simpleDatum2,
                        datum1,
                        datum2,
                        reverse,
                        sortTiebreaker
                    );
                }
            }
            if (sortable !== undefined) {
                const sortValue = sortable(datum1, datum2);
                if (sortValue === 0)
                    return tiebreakerControler(sortTiebreaker, datum1, datum2);
                return reverse(sortValue);
            }
            return 0;
        });

    // Updated filtering logic.
    const filterDataArray = (data: T[]) => {
        let dataArray = data;
        for (const col of columns) {
            const { filter } = col;
            if (filter === undefined) continue;
            const { id, type, stringValue, multiple } = filter;
            const filterState = filterInfoMap[id];
            if (filterState === undefined) continue;

            // NEW: Handle multi-select filter.
            if (multiple) {
                const selectedValues = (filterState as { type: 'multiselect'; value: string[] }).value;
                if (
                    !selectedValues ||
                    selectedValues.length === 0 ||
                    selectedValues.includes(ALL_OPTIONS_VALUE)
                )
                    continue;
                dataArray = dataArray.filter(datum => {
                    const datumValue = stringValue(datum);
                    if (datumValue === null) return false;
                    const rowStatuses = datumValue.split(',').map(s => s.trim());
                    return selectedValues.some(status => rowStatuses.includes(status));
                });
            } else {
                const currentValue = (filterState as { type: 'select' | 'string'; value: string }).value;
                if (currentValue === '') continue;
                if (type === 'string') {
                    dataArray = dataArray.filter(datum => {
                        const datumString = stringValue(datum)?.toLowerCase();
                        return datumString?.includes(currentValue.toLowerCase());
                    });
                } else {
                    if (currentValue === ALL_OPTIONS_VALUE) continue;
                    dataArray = dataArray.filter(
                        datum => stringValue(datum) === currentValue
                    );
                }
            }
        }
        return dataArray;
    };

    // Display functions.
    const displayHead = () => (
        <thead>
            <tr>
                {columns.map(({ title, textAlign, sortable, simpleSortable, filter, cellClassName }, index) => {
                    const isSortable = sortable !== undefined || simpleSortable !== undefined;
                    const hasFilter = filter !== undefined;
                    return (
                        <th
                            key={index}
                            className={cx(cellClassName, {
                                'is-sortable': isSortable,
                                [`align-${textAlign ?? 'right'}`]: true
                            })}
                            onClick={() => {
                                if (!hasFilter) {
                                    setSortingInfo({
                                        sortColumnIndex:
                                            sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort
                                                ? null
                                                : index,
                                        sortColumnTitle:
                                            sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort
                                                ? null
                                                : title,
                                        reverseSort:
                                            sortingInfo.sortColumnIndex === index
                                                ? !sortingInfo.reverseSort
                                                : false
                                    });
                                }
                            }}
                        >
                            <div className='content-wrapper'>
                                <div
                                    className='title-span'
                                    onClick={() => {
                                        if (hasFilter) {
                                            setSortingInfo({
                                                sortColumnIndex:
                                                    sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort
                                                        ? null
                                                        : index,
                                                sortColumnTitle:
                                                    sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort
                                                        ? null
                                                        : title,
                                                reverseSort:
                                                    sortingInfo.sortColumnIndex === index
                                                        ? !sortingInfo.reverseSort
                                                        : false
                                            });
                                        }
                                    }}
                                >
                                    <span className='text-span'>
                                        {isSortable && sortingInfo.sortColumnIndex === index ? (
                                            sortingInfo.reverseSort ? (
                                                <span className='caret-span is-descending'>
                                                    <FontAwesomeIcon icon={faCaretDown} />
                                                </span>
                                            ) : (
                                                <span className='caret-span is-ascending'>
                                                    <FontAwesomeIcon icon={faCaretUp} />
                                                </span>
                                            )
                                        ) : null}
                                        {title}
                                    </span>
                                </div>
                                <div className={cx('form-wrapper', textAlign ?? 'right')}>
                                    {filter === undefined ? null : (
                                        filter.multiple ? (
                                            <MultiSelectFilter 
                                                filter={filter} 
                                                filterInfoMap={filterInfoMap} 
                                                setFilterInfoMap={setFilterInfoMap}
                                            />
                                        ) : filter.type === 'string' ? (
                                            <Form.Control
                                                value={
                                                    ((filterInfoMap[filter.id] as { type: 'string' | 'select'; value: string })
                                                        ?.value) ||
                                                    filter.forcedValue ||
                                                    ''
                                                }
                                                onChange={(e) => {
                                                    if (filter.onChange) filter.onChange(e.target.value);
                                                    setFilterInfoMap({
                                                        ...filterInfoMap,
                                                        [filter.id]: { type: 'string', value: e.target.value }
                                                    });
                                                }}
                                            />
                                        ) : (
                                            <Form.Control
                                                as="select"
                                                value={
                                                    ((filterInfoMap[filter.id] as { type: 'string' | 'select'; value: string })
                                                        ?.value) ||
                                                    filter.forcedValue ||
                                                    ALL_OPTIONS_VALUE
                                                }
                                                onChange={(e) => {
                                                    if (filter.onChange) filter.onChange(e.target.value);
                                                    setFilterInfoMap({
                                                        ...filterInfoMap,
                                                        [filter.id]: { type: 'select', value: e.target.value }
                                                    });
                                                }}
                                            >
                                                {[ALL_OPTIONS_VALUE].concat(filter.type as string[]).map(option => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        )
                                    )}
                                </div>
                            </div>
                        </th>
                    );
                })}
            </tr>
        </thead>
    );

    const RenderNoClickBody = ({ children }: { children: React.ReactNode }) => (
        <tbody>
            <tr className='NoClick'>
                <td className='text-left' colSpan={columns.length}>
                    {children}
                </td>
            </tr>
        </tbody>
    );

    const EmptyBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        <RenderNoClickBody>{children}</RenderNoClickBody>
    );

    const displayDataBody = () => (
        <tbody>
            {sortDataArray(filterDataArray(data)).map((datum, index) => (
                <tr
                    key={index}
                    onClick={() => {
                        if (onRowClick) onRowClick(datum);
                    }}
                    id={rowId ? rowId(datum) : undefined}
                    className={rowClassName ? rowClassName(datum) : undefined}
                >
                    {columns.map(({ dataAccessor, textAlign, overrideTd, cellClassName, isColorTimestamp }, colIndex) =>
                        overrideTd ? (
                            <Fragment key={colIndex}>{dataAccessor(datum)}</Fragment>
                        ) : (
                            <td
                                key={colIndex}
                                className={cx(cellClassName, `align-${textAlign ?? 'right'}`, {
                                    'color-timestamp': isColorTimestamp && isColorTimestamp(datum)
                                })}
                            >
                                {dataAccessor(datum)}
                            </td>
                        )
                    )}
                </tr>
            ))}
        </tbody>
    );

    const displayBody = () =>
        isLoading ? (
            <EmptyBody>Loading...</EmptyBody>
        ) : isError ? (
            <EmptyBody>
                Something went wrong... Try again or contact help@kodi.is
            </EmptyBody>
        ) : data.length === 0 ? (
            <EmptyBody>No Data.</EmptyBody>
        ) : (
            displayDataBody()
        );

    return (
        <Table className={cx('CompressedTable SortFilterTable', { 'is-filterable': isFilterable })}>
            {displayHead()}
            {displayBody()}
        </Table>
    );
};

export default SortFilterTable;
